import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Slider,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DataObjectIcon from "@mui/icons-material/DataObject";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableChartIcon from "@mui/icons-material/TableChart";
import Editor from "@monaco-editor/react";

const TAB_ITEMS = [
  { id: "payload", label: "Payload" },
  { id: "derived_prompts", label: "Derived Prompts" },
  { id: "derived", label: "Derived Categorization" },
  { id: "output_schema_prompts", label: "Output Schema Prompts" },
  { id: "cost_model_results", label: "Cost Model Results" },
  { id: "comparison", label: "Cost Variance" },
];

const FLOAT_PRECISION = 4;
const FLOAT_TOKEN_PREFIX = "__AI_FLOAT4__";

const toLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text) return value;
  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
};

const isJsonValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "object") return true;
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (!text) return false;
  try {
    const parsed = JSON.parse(text);
    return parsed !== null && typeof parsed === "object";
  } catch {
    return false;
  }
};

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
const isFloatNumber = (value) => isFiniteNumber(value) && !Number.isInteger(value);

const convertFloatsToTokens = (value) => {
  if (isFloatNumber(value)) {
    return `${FLOAT_TOKEN_PREFIX}${value.toFixed(FLOAT_PRECISION)}`;
  }
  if (Array.isArray(value)) {
    return value.map((item) => convertFloatsToTokens(item));
  }
  if (value && typeof value === "object") {
    const output = {};
    Object.entries(value).forEach(([key, nestedValue]) => {
      output[key] = convertFloatsToTokens(nestedValue);
    });
    return output;
  }
  return value;
};

const stringifyJsonWithFixedFloats = (value) => {
  const tokenized = convertFloatsToTokens(value);
  const jsonText = JSON.stringify(tokenized, null, 2);
  const escapedTokenPrefix = FLOAT_TOKEN_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokenRegex = new RegExp(`"${escapedTokenPrefix}(-?\\d+\\.\\d{${FLOAT_PRECISION}})"`, "g");
  return jsonText.replace(tokenRegex, "$1");
};

const toJsonEditorValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return "";
    try {
      return stringifyJsonWithFixedFloats(JSON.parse(text));
    } catch {
      return value;
    }
  }
  try {
    return stringifyJsonWithFixedFloats(value);
  } catch {
    return String(value);
  }
};

const MAX_JSON_TABLE_ROWS = 2000;
const DEFAULT_JSON_TABLE_COL_WIDTH = 160;
const MIN_JSON_TABLE_COL_WIDTH = 90;
const MAX_JSON_TABLE_COL_WIDTH = 720;
const PROMPT_TEXT_FONT_FAMILY =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";
const isHiddenJsonTableColumn = (column) =>
  String(column || "")
    .replace(/[\s_]+/g, "")
    .trim()
    .toLowerCase() === "projectid";

const formatTableCellValue = (value) => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  if (isFloatNumber(value)) {
    return value.toFixed(FLOAT_PRECISION);
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  try {
    return stringifyJsonWithFixedFloats(value);
  } catch {
    return String(value);
  }
};

const buildJsonTableDataset = (value) => {
  const parsed = tryParseJson(value);

  if (Array.isArray(parsed)) {
    const rows = parsed.slice(0, MAX_JSON_TABLE_ROWS);
    const truncated = parsed.length > MAX_JSON_TABLE_ROWS;

    const allObjects = rows.every((item) => item && typeof item === "object" && !Array.isArray(item));
    if (allObjects && rows.length > 0) {
      const keySet = new Set();
      rows.forEach((item) => {
        Object.keys(item || {}).forEach((key) => {
          if (!isHiddenJsonTableColumn(key)) {
            keySet.add(key);
          }
        });
      });
      const columns = Array.from(keySet);
      if (!columns.length) {
        return {
          columns: ["Value"],
          rows: rows.map((item) => {
            const sanitized = {};
            Object.keys(item || {}).forEach((key) => {
              if (!isHiddenJsonTableColumn(key)) {
                sanitized[key] = item[key];
              }
            });
            return { Value: formatTableCellValue(sanitized) };
          }),
          truncated,
        };
      }
      const normalizedRows = rows.map((item) => {
        const row = {};
        columns.forEach((column) => {
          row[column] = formatTableCellValue(item?.[column]);
        });
        return row;
      });
      return {
        columns: columns.length ? columns : ["Value"],
        rows: normalizedRows,
        truncated,
      };
    }

    return {
      columns: ["Value"],
      rows: rows.map((item) => ({ Value: formatTableCellValue(item) })),
      truncated,
    };
  }

  if (parsed !== null && typeof parsed === "object") {
    const columns = Object.keys(parsed).filter((column) => !isHiddenJsonTableColumn(column));
    if (!columns.length) {
      const sanitized = {};
      Object.keys(parsed || {}).forEach((column) => {
        if (!isHiddenJsonTableColumn(column)) {
          sanitized[column] = parsed[column];
        }
      });
      return {
        columns: ["Value"],
        rows: [{ Value: formatTableCellValue(sanitized) }],
        truncated: false,
      };
    }

    const row = {};
    columns.forEach((column) => {
      row[column] = formatTableCellValue(parsed[column]);
    });

    return {
      columns,
      rows: [row],
      truncated: false,
    };
  }

  return {
    columns: ["Value"],
    rows: [{ Value: formatTableCellValue(parsed) }],
    truncated: false,
  };
};

const normalizePromptEntries = (raw) => {
  const parsed = tryParseJson(raw);
  if (!parsed) return [];

  if (Array.isArray(parsed)) {
    return parsed.map((item) => (item && typeof item === "object" ? item : { value: item }));
  }

  if (parsed && typeof parsed === "object") {
    const candidateArrayKeys = [
      "derived_prompt_logs",
      "derivedPrompts",
      "prompts",
      "entries",
      "items",
      "logs",
      "calls",
      "results",
      "data",
    ];
    for (const key of candidateArrayKeys) {
      if (Array.isArray(parsed[key])) {
        return parsed[key].map((item) => (item && typeof item === "object" ? item : { value: item }));
      }
    }
    return [parsed];
  }

  return [{ value: parsed }];
};

const readField = (entry, keys) => {
  for (const key of keys) {
    if (entry?.[key] !== undefined && entry?.[key] !== null) return entry[key];
  }
  return null;
};

const formatComparisonCost = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toFixed(4);
};

const formatComparisonDelta = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${Math.round(numeric)}%`;
};

const formatComparisonDeltaValue = (currentValue, baselineValue) => {
  const current = Number(currentValue);
  const baseline = Number(baselineValue);
  if (!Number.isFinite(current) || !Number.isFinite(baseline)) return "";
  const delta = current - baseline;
  const prefix = delta > 0 ? "+" : "";
  return `${prefix}${delta.toFixed(4)}`;
};

const formatComparisonShare = (value, total) => {
  const numeric = Number(value);
  const denominator = Number(total);
  if (!Number.isFinite(numeric) || !Number.isFinite(denominator) || denominator === 0) return "";
  return `${((numeric / denominator) * 100).toFixed(1)}%`;
};

const renderComparisonHeader = (primary, secondary = "") => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1, textAlign: "center" }}>
    <Box component="span">{primary}</Box>
    {secondary ? <Box component="span" sx={{ mt: 0.15 }}>{secondary}</Box> : null}
  </Box>
);

export default function AdminAnalysisPopup({
  open,
  onClose,
  schemaTypes = [],
  schemaDefinitionColumns = [],
  schemaDefinitions = [],
  derivedSchemaDefinitionColumns = [],
  derivedSchemaDefinitions = [],
  frontendReturnItems = [],
  statusMessage = "",
  requestPayload = null,
  derivedConstraints = null,
  derivedPrompts = null,
  outputSchemaPrompts = null,
  costModelResults = null,
  comparisonData = null,
  derivedResult = null,
  perlColors,
  inline = false,
  title = "Trace",
  onAddRun = null,
  onRefresh = null,
  onCopySqlScript = null,
  copySqlScriptDisabled = false,
  copySqlScriptTooltip = "Copy SQL script",
  toolbarInfo = "",
}) {
  const [activeTab, setActiveTab] = useState("payload");
  const [activeDerivedPromptIndex, setActiveDerivedPromptIndex] = useState(0);
  const [activeDerivedPromptSection, setActiveDerivedPromptSection] = useState("");
  const [activeOutputPromptIndex, setActiveOutputPromptIndex] = useState(0);
  const [activeOutputPromptSection, setActiveOutputPromptSection] = useState("");
  const [activeCostModelResultKey, setActiveCostModelResultKey] = useState("");
  const [varianceThreshold, setVarianceThreshold] = useState(50);
  const [jsonViewModes, setJsonViewModes] = useState({});
  const [jsonTableColumnWidths, setJsonTableColumnWidths] = useState({});
  const [cellDialogOpen, setCellDialogOpen] = useState(false);
  const columnWidthScale = 1;
  const [selectedCell, setSelectedCell] = useState({
    column: "",
    value: "",
    isJson: false,
  });

  useEffect(() => {
    if (open) {
      setActiveTab("payload");
    }
  }, [open]);

  const panelTitle = useMemo(() => {
    if (activeTab === "payload") return "Payload Passed";
    if (activeTab === "derived") return "Derived Categorization";
    if (activeTab === "derived_prompts") return "Derived Prompts";
    if (activeTab === "output_schema_prompts") return "Output Schema Prompts";
    if (activeTab === "cost_model_results") return "Cost Model Results";
    if (activeTab === "comparison") return "Cost Variance";
    if (activeTab === "derived_schema_definitions") return "Derived Schema Definitions";
    if (activeTab === "frontend_returns") return "Returned To Frontend";
    return "Payload Passed";
  }, [activeTab]);

  const derivedCategorizationData = useMemo(() => {
    if (derivedConstraints !== null && derivedConstraints !== undefined) {
      return derivedConstraints;
    }
    return derivedResult;
  }, [derivedConstraints, derivedResult]);

  const derivedPromptEntries = useMemo(
    () => normalizePromptEntries(derivedPrompts),
    [derivedPrompts],
  );

  const outputPromptEntries = useMemo(
    () => normalizePromptEntries(outputSchemaPrompts),
    [outputSchemaPrompts],
  );

  const costModelResultEntries = useMemo(() => {
    const parsed = tryParseJson(costModelResults);
    if (parsed === null || parsed === undefined || parsed === "") return [];

    if (Array.isArray(parsed)) {
      return parsed.map((item, index) => ({
        id: `item_${index}`,
        label: `Result ${index + 1}`,
        value: item,
      }));
    }

    if (parsed && typeof parsed === "object") {
      const preferredOrder = [
        "DescriptionResult",
        "FormulationResult",
        "GoodsReturnResult",
        "MaterialResult",
        "ProcessResult",
        "PackagingResult",
        "LogisticsResult",
        "BomResult",
        "ShouldCostResult",
        "ProjectID",
        "PartKey",
      ];
      const orderIndex = preferredOrder.reduce((acc, key, index) => {
        acc[key.toLowerCase()] = index;
        return acc;
      }, {});

      return Object.keys(parsed)
        .sort((a, b) => {
          const ai = orderIndex[String(a).toLowerCase()];
          const bi = orderIndex[String(b).toLowerCase()];
          if (ai !== undefined && bi !== undefined) return ai - bi;
          if (ai !== undefined) return -1;
          if (bi !== undefined) return 1;
          return String(a).localeCompare(String(b));
        })
        .map((key) => ({
          id: String(key),
          label: String(key),
          value: parsed[key],
        }));
    }

    return [
      {
        id: "value",
        label: "Value",
        value: parsed,
      },
    ];
  }, [costModelResults]);

  const derivedPromptTabItems = useMemo(() => {
    return derivedPromptEntries.map((entry, index) => {
      const order = readField(entry, ["order", "step", "index", "sequence"]);
      const schemaType = readField(entry, ["schema_type", "schemaType", "component", "name"]);
      const baseLabel = schemaType ? String(schemaType) : `Prompt ${index + 1}`;
      const rawLabel =
        order !== null && order !== undefined && order !== ""
          ? `${order}. ${baseLabel}`
          : baseLabel;
      const label = rawLabel.length > 28 ? `${rawLabel.slice(0, 28)}...` : rawLabel;
      return {
        value: index,
        label,
      };
    });
  }, [derivedPromptEntries]);

  const outputPromptTabItems = useMemo(() => {
    return outputPromptEntries.map((entry, index) => {
      const order = readField(entry, ["order", "step", "index", "sequence"]);
      const schemaType = readField(entry, ["schema_type", "schemaType", "component", "name"]);
      const baseLabel = schemaType ? String(schemaType) : `Prompt ${index + 1}`;
      const rawLabel =
        order !== null && order !== undefined && order !== ""
          ? `${order}. ${baseLabel}`
          : baseLabel;
      const label = rawLabel.length > 28 ? `${rawLabel.slice(0, 28)}...` : rawLabel;
      return {
        value: index,
        label,
      };
    });
  }, [outputPromptEntries]);

  useEffect(() => {
    if (derivedPromptEntries.length === 0) {
      setActiveDerivedPromptIndex(0);
      return;
    }
    if (activeDerivedPromptIndex >= derivedPromptEntries.length) {
      setActiveDerivedPromptIndex(0);
    }
  }, [activeDerivedPromptIndex, derivedPromptEntries]);

  useEffect(() => {
    if (outputPromptEntries.length === 0) {
      setActiveOutputPromptIndex(0);
      return;
    }
    if (activeOutputPromptIndex >= outputPromptEntries.length) {
      setActiveOutputPromptIndex(0);
    }
  }, [activeOutputPromptIndex, outputPromptEntries]);

  useEffect(() => {
    if (!costModelResultEntries.length) {
      setActiveCostModelResultKey("");
      return;
    }
    if (!costModelResultEntries.some((entry) => entry.id === activeCostModelResultKey)) {
      setActiveCostModelResultKey(costModelResultEntries[0].id);
    }
  }, [activeCostModelResultKey, costModelResultEntries]);

  const resolvedSchemaDefinitionColumns = useMemo(() => {
    if (Array.isArray(schemaDefinitionColumns) && schemaDefinitionColumns.length > 0) {
      return schemaDefinitionColumns;
    }
    if (!Array.isArray(schemaDefinitions) || schemaDefinitions.length === 0) return [];
    return Object.keys(schemaDefinitions[0] || {});
  }, [schemaDefinitionColumns, schemaDefinitions]);

  const schemaDefinitionColumnWidths = useMemo(() => {
    const widths = {};
    for (const column of resolvedSchemaDefinitionColumns) {
      let maxLen = String(column || "").length;
      for (const row of schemaDefinitions) {
        const rawValue = row?.[column];
        const text =
          rawValue === null || rawValue === undefined
            ? "-"
            : typeof rawValue === "string"
              ? rawValue
              : JSON.stringify(rawValue);
        if (text.length > maxLen) {
          maxLen = text.length;
        }
      }

      const compactWidth = Math.max(88, Math.min(180, maxLen * 7 + 20));
      widths[column] = Math.max(88, Math.min(420, Math.round(compactWidth * columnWidthScale)));
    }
    return widths;
  }, [resolvedSchemaDefinitionColumns, schemaDefinitions, columnWidthScale]);

  const resolvedDerivedSchemaDefinitionColumns = useMemo(() => {
    if (Array.isArray(derivedSchemaDefinitionColumns) && derivedSchemaDefinitionColumns.length > 0) {
      return derivedSchemaDefinitionColumns;
    }
    if (!Array.isArray(derivedSchemaDefinitions) || derivedSchemaDefinitions.length === 0) return [];
    return Object.keys(derivedSchemaDefinitions[0] || {});
  }, [derivedSchemaDefinitionColumns, derivedSchemaDefinitions]);

  const derivedSchemaDefinitionColumnWidths = useMemo(() => {
    const widths = {};
    for (const column of resolvedDerivedSchemaDefinitionColumns) {
      let maxLen = String(column || "").length;
      for (const row of derivedSchemaDefinitions) {
        const rawValue = row?.[column];
        const text =
          rawValue === null || rawValue === undefined
            ? "-"
            : typeof rawValue === "string"
              ? rawValue
              : JSON.stringify(rawValue);
        if (text.length > maxLen) {
          maxLen = text.length;
        }
      }

      const compactWidth = Math.max(88, Math.min(180, maxLen * 7 + 20));
      widths[column] = Math.max(88, Math.min(420, Math.round(compactWidth * columnWidthScale)));
    }
    return widths;
  }, [resolvedDerivedSchemaDefinitionColumns, derivedSchemaDefinitions, columnWidthScale]);

  const preStyles = {
    m: 0,
    p: 1.5,
    borderRadius: 1.5,
    bgcolor: "rgba(0,0,0,0.2)",
    border: `1px solid ${perlColors.border}`,
    color: perlColors.textSecondary,
    fontSize: "0.85rem",
    lineHeight: 1.45,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };

  const handleOpenCellDialog = (column, value) => {
    const jsonValue = isJsonValue(value);
    setSelectedCell({
      column: column || "",
      value: jsonValue
        ? toJsonEditorValue(value)
        : value === null || value === undefined
          ? "-"
          : typeof value === "string"
            ? value
            : JSON.stringify(value, null, 2),
      isJson: jsonValue,
    });
    setCellDialogOpen(true);
  };

  const handleCloseCellDialog = () => {
    setCellDialogOpen(false);
  };

  const activeTabLabel = useMemo(
    () => TAB_ITEMS.find((item) => item.id === activeTab)?.label || "Payload",
    [activeTab],
  );

  const actionIconSx = {
    color: perlColors.textSecondary,
    border: `1px solid ${perlColors.border}`,
    width: 28,
    height: 28,
  };

  const toolbarIconSx = {
    color: perlColors.textPrimary,
    "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
  };

  const renderHeaderBar = (showClose = false) => (
    <Box
      sx={{
        px: 2,
        pt: 1,
        pb: 1,
        borderBottom: `1px solid ${perlColors.border}`,
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      <Box
        sx={{
          mt: 1,
          border: `1px solid ${perlColors.border}`,
          borderRadius: "10px",
          px: 0.75,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: "rgba(255, 255, 255, 0.02)",
        }}
      >
        {typeof onAddRun === "function" && (
          <Tooltip title="Add Run">
            <IconButton size="small" onClick={onAddRun} sx={toolbarIconSx}>
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {typeof onRefresh === "function" && (
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} sx={toolbarIconSx}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {typeof onCopySqlScript === "function" && (
          <Tooltip title={copySqlScriptTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={onCopySqlScript}
                disabled={copySqlScriptDisabled}
                sx={toolbarIconSx}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Typography sx={{ ml: 1, fontSize: "0.75rem", color: perlColors.textSecondary }}>
          Section: {activeTabLabel}
        </Typography>

        {!!toolbarInfo && (
          <Typography sx={{ ml: 1, fontSize: "0.75rem", color: perlColors.textSecondary }}>
            {toolbarInfo}
          </Typography>
        )}

        {showClose && (
          <Tooltip title="Close">
            <IconButton onClick={onClose} size="small" sx={{ ...toolbarIconSx, ml: "auto" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const renderDefinitionTable = (rows, columns, columnWidths) => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1.5,
          bgcolor: "rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            height: "100%",
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: "fixed",
              minWidth: `${columns.reduce(
                (sum, column) => sum + (columnWidths[column] || 120),
                0,
              )}px`,
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: perlColors.textPrimary,
                      borderBottom: `1px solid ${perlColors.border}`,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      py: 0.6,
                      fontSize: "0.78rem",
                      width: columnWidths[column] || 120,
                      minWidth: columnWidths[column] || 120,
                      maxWidth: columnWidths[column] || 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={`schema-def-row-${rowIndex}`}>
                  {columns.map((column) => (
                    <TableCell
                      key={`${rowIndex}-${column}`}
                      onDoubleClick={() => handleOpenCellDialog(column, row?.[column])}
                      sx={{
                        color: perlColors.textSecondary,
                        borderBottom: `1px solid ${perlColors.border}`,
                        py: 0.6,
                        fontSize: "0.76rem",
                        whiteSpace: "nowrap",
                        width: columnWidths[column] || 120,
                        minWidth: columnWidths[column] || 120,
                        maxWidth: columnWidths[column] || 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "zoom-in",
                      }}
                    >
                      {row?.[column] === null || row?.[column] === undefined ? "-" : String(row[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  const renderReadOnlyMonaco = (value, language = "json") => (
    <Editor
      height="100%"
      defaultLanguage={language}
      value={value}
      theme="vs-dark"
      options={{
        readOnly: true,
        minimap: { enabled: false },
        fontSize: 13,
        lineNumbers: "on",
        scrollBeyondLastLine: false,
        automaticLayout: true,
        wordWrap: "on",
      }}
    />
  );

  const getJsonViewMode = (viewKey) => (jsonViewModes[viewKey] === "table" ? "table" : "json");

  const setJsonViewMode = (viewKey, mode) => {
    setJsonViewModes((prev) => {
      const current = prev[viewKey] === "table" ? "table" : "json";
      if (current === mode) return prev;
      return { ...prev, [viewKey]: mode };
    });
  };

  const getJsonTableColumnWidth = (viewKey, column) => {
    const width = jsonTableColumnWidths?.[viewKey]?.[column];
    if (typeof width === "number" && Number.isFinite(width)) {
      return Math.max(MIN_JSON_TABLE_COL_WIDTH, Math.min(MAX_JSON_TABLE_COL_WIDTH, width));
    }
    return DEFAULT_JSON_TABLE_COL_WIDTH;
  };

  const setJsonTableColumnWidth = (viewKey, column, width) => {
    const normalizedWidth = Math.max(
      MIN_JSON_TABLE_COL_WIDTH,
      Math.min(MAX_JSON_TABLE_COL_WIDTH, Math.round(Number(width) || DEFAULT_JSON_TABLE_COL_WIDTH)),
    );
    setJsonTableColumnWidths((prev) => {
      const currentWidth = prev?.[viewKey]?.[column];
      if (currentWidth === normalizedWidth) return prev;
      return {
        ...prev,
        [viewKey]: {
          ...(prev?.[viewKey] || {}),
          [column]: normalizedWidth,
        },
      };
    });
  };

  const handleJsonTableColumnResizeStart = (event, viewKey, column) => {
    event.preventDefault();
    event.stopPropagation();
    if (!viewKey || !column) return;

    const startX = Number(event.clientX || 0);
    const startWidth = getJsonTableColumnWidth(viewKey, column);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (moveEvent) => {
      const currentX = Number(moveEvent.clientX || 0);
      const delta = currentX - startX;
      setJsonTableColumnWidth(viewKey, column, startWidth + delta);
    };

    const stopResize = () => {
      window.removeEventListener("mousemove", onMouseMove);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener(
      "mouseup",
      () => {
        stopResize();
      },
      { once: true },
    );
  };

  const renderJsonViewToggle = (viewKey) => {
    const mode = getJsonViewMode(viewKey);
    const toggleButtonSx = (selected) => ({
      width: 24,
      height: 24,
      border: `1px solid ${selected ? "#ff6034" : perlColors.border}`,
      borderRadius: 1,
      color: selected ? perlColors.textPrimary : perlColors.textSecondary,
      backgroundColor: selected ? "rgba(255,96,52,0.18)" : "transparent",
      "&:hover": {
        backgroundColor: "rgba(255,96,52,0.12)",
      },
    });

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="JSON View">
          <IconButton
            size="small"
            onClick={() => setJsonViewMode(viewKey, "json")}
            sx={toggleButtonSx(mode === "json")}
          >
            <DataObjectIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Table View">
          <IconButton
            size="small"
            onClick={() => setJsonViewMode(viewKey, "table")}
            sx={toggleButtonSx(mode === "table")}
          >
            <TableChartIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderJsonAsTable = (value, viewKey = "json_default") => {
    const dataset = buildJsonTableDataset(value);
    const columns = dataset?.columns || [];
    const rows = dataset?.rows || [];
    const truncated = !!dataset?.truncated;
    const totalTableWidth = Math.max(
      columns.reduce((sum, column) => sum + getJsonTableColumnWidth(viewKey, column), 0),
      420,
    );
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {truncated && (
          <Typography
            sx={{
              px: 1,
              py: 0.45,
              fontSize: "0.72rem",
              color: perlColors.textSecondary,
              borderBottom: `1px solid ${perlColors.border}`,
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          >
            Showing first {MAX_JSON_TABLE_ROWS} rows only.
          </Typography>
        )}
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            overflowX: "scroll",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.35) rgba(255,255,255,0.08)",
            "&::-webkit-scrollbar": {
              width: 10,
              height: 12,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.35)",
              borderRadius: "8px",
              border: "2px solid rgba(0,0,0,0.25)",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: "8px",
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: "fixed",
              width: `${totalTableWidth}px`,
              minWidth: `${totalTableWidth}px`,
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  (() => {
                    const columnWidth = getJsonTableColumnWidth(viewKey, column);
                    return (
                      <TableCell
                        key={`json-col-${column}`}
                        sx={{
                          position: "relative",
                          backgroundColor: "rgba(255,255,255,0.06)",
                          color: perlColors.textPrimary,
                          borderBottom: `1px solid ${perlColors.border}`,
                          fontWeight: 600,
                          fontSize: "0.76rem",
                          py: 0.6,
                          pr: 1.5,
                          width: columnWidth,
                          minWidth: columnWidth,
                          maxWidth: columnWidth,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {column}
                        <Box
                          onMouseDown={(event) =>
                            handleJsonTableColumnResizeStart(event, viewKey, column)
                          }
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 8,
                            height: "100%",
                            cursor: "col-resize",
                            userSelect: "none",
                            touchAction: "none",
                            zIndex: 3,
                            "&:hover": {
                              backgroundColor: "rgba(255,96,52,0.2)",
                            },
                          }}
                        />
                      </TableCell>
                    );
                  })()
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map((row, rowIndex) => (
                  <TableRow key={`json-row-${rowIndex}`}>
                    {columns.map((column) => {
                      const columnWidth = getJsonTableColumnWidth(viewKey, column);
                      return (
                        <TableCell
                          key={`json-row-${rowIndex}-${column}`}
                          sx={{
                            color: perlColors.textPrimary,
                            borderBottom: `1px solid ${perlColors.border}`,
                            fontFamily: PROMPT_TEXT_FONT_FAMILY,
                            fontSize: "0.78rem",
                            py: 0.6,
                            width: columnWidth,
                            minWidth: columnWidth,
                            maxWidth: columnWidth,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.35,
                            height: 34,
                            verticalAlign: "top",
                          }}
                        >
                          {row?.[column] ?? ""}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(columns.length, 1)}
                    sx={{
                      color: perlColors.textSecondary,
                      borderBottom: `1px solid ${perlColors.border}`,
                      fontSize: "0.74rem",
                      py: 1,
                    }}
                  >
                    No rows available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderJsonByMode = (value, viewKey) => {
    if (getJsonViewMode(viewKey) === "table") {
      return renderJsonAsTable(value, viewKey);
    }
    return renderReadOnlyMonaco(toJsonEditorValue(value), "json");
  };

  const renderJsonPanelBody = (value, viewKey) => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {renderJsonByMode(value, viewKey)}
    </Box>
  );

  const renderJsonPanelWithToggle = (value, viewKey, showDivider = true) => (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 1.1,
          py: 0.45,
          borderBottom: showDivider ? `1px solid ${perlColors.border}` : "none",
          backgroundColor: "rgba(255,255,255,0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {renderJsonViewToggle(viewKey)}
      </Box>
      {renderJsonPanelBody(value, viewKey)}
    </Box>
  );

  const renderPromptTextPanel = (titleText, value, tone = "default") => {
    if (value === null || value === undefined || value === "") return null;
    const color =
      tone === "error" ? "#ff9b9b" : tone === "accent" ? perlColors.textPrimary : perlColors.textSecondary;
    return (
      <Box
        sx={{
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1.25,
          overflow: "hidden",
          bgcolor: "rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          flex: 1,
          alignSelf: "stretch",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            px: 1.1,
            py: 0.65,
            borderBottom: `1px solid ${perlColors.border}`,
            backgroundColor: "rgba(255,255,255,0.03)",
          }}
        >
          <Typography sx={{ fontSize: "0.76rem", color: perlColors.textPrimary, fontWeight: 600 }}>
            {titleText}
          </Typography>
        </Box>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.1,
            color,
            fontFamily: PROMPT_TEXT_FONT_FAMILY,
            fontSize: "0.78rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
        </Box>
      </Box>
    );
  };

  const renderPromptJsonPanel = (titleText, value, viewKey = "json_default") => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <Box
        sx={{
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1.25,
          overflow: "hidden",
          bgcolor: "rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          flex: 1,
          alignSelf: "stretch",
          width: "100%",
          maxWidth: "100%",
        }}
      >
      <Box
        sx={{
          px: 1.1,
          py: 0.65,
          borderBottom: `1px solid ${perlColors.border}`,
          backgroundColor: "rgba(255,255,255,0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: 0,
        }}
      >
          <Typography
            sx={{
              fontSize: "0.76rem",
              color: perlColors.textPrimary,
              fontWeight: 600,
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {titleText}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>{renderJsonViewToggle(viewKey)}</Box>
        </Box>
        {renderJsonPanelBody(value, viewKey)}
      </Box>
    );
  };

  const renderPromptGeneralPanel = (items = []) => {
    const safeItems = Array.isArray(items) ? items.filter((item) => item && item.value !== "") : [];
    return (
      <Box
        sx={{
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1.25,
          overflow: "hidden",
          bgcolor: "rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          flex: 1,
          alignSelf: "stretch",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            px: 1.1,
            py: 0.65,
            borderBottom: `1px solid ${perlColors.border}`,
            backgroundColor: "rgba(255,255,255,0.03)",
          }}
        >
          <Typography sx={{ fontSize: "0.76rem", color: perlColors.textPrimary, fontWeight: 600 }}>
            General
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 0.8 }}>
          {safeItems.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.45 }}>
              {safeItems.map((item, idx) => {
                const tone = item?.tone || "default";
                const valueText =
                  typeof item?.value === "string" ? item.value : JSON.stringify(item?.value, null, 2);
                const valueNode =
                  tone === "status" ? (
                    <Chip
                      size="small"
                      label={valueText}
                      color={
                        /success/i.test(String(valueText))
                          ? "success"
                          : /error|fail/i.test(String(valueText))
                            ? "error"
                            : "default"
                      }
                      variant="outlined"
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: tone === "error" ? "#ff9b9b" : perlColors.textPrimary,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.35,
                      }}
                    >
                      {valueText}
                    </Typography>
                  );

                return (
                  <Box
                    key={item?.id || `general-item-${idx}`}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.8,
                      borderBottom:
                        idx === safeItems.length - 1 ? "none" : `1px solid ${perlColors.border}`,
                      pb: 0.5,
                      pt: 0.15,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: perlColors.textSecondary,
                        minWidth: 118,
                        maxWidth: 118,
                        flexShrink: 0,
                        pt: 0.15,
                      }}
                    >
                      {item?.label}
                    </Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>{valueNode}</Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ color: perlColors.textSecondary, fontSize: "0.78rem" }}>
              No metadata available for this prompt.
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const buildDerivedPromptDetail = (entry, index) => {
    if (!entry || typeof entry !== "object") return null;

    const used = new Set();
    const hiddenFieldKeys = new Set([
      "original_output_response_format",
      "effective_output_response_format",
      "derived_category_schema",
    ]);
    const markUsed = (keys, value) => {
      if (value === null || value === undefined || value === "") return;
      keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(entry || {}, key)) {
          used.add(key);
        }
      });
    };

    const order = readField(entry, ["order", "step", "index", "sequence"]);
    markUsed(["order", "step", "index", "sequence"], order);
    const schemaType = readField(entry, ["schema_type", "schemaType", "component", "name"]);
    markUsed(["schema_type", "schemaType", "component", "name"], schemaType);
    const statusRaw = readField(entry, ["status", "result_status", "state"]);
    markUsed(["status", "result_status", "state"], statusRaw);
    const model = readField(entry, ["model", "llm_model"]);
    markUsed(["model", "llm_model"], model);
    const temperature = readField(entry, ["temperature", "temp"]);
    markUsed(["temperature", "temp"], temperature);
    const systemPrompt = readField(entry, [
      "system_prompt",
      "systemPrompt",
      "prompt_system",
      "system",
    ]);
    markUsed(["system_prompt", "systemPrompt", "prompt_system", "system"], systemPrompt);
    const userPromptRendered = readField(entry, [
      "user_prompt_rendered",
      "userPromptRendered",
      "user_prompt",
      "userPrompt",
      "prompt_user",
      "user",
    ]);
    markUsed(
      ["user_prompt_rendered", "userPromptRendered", "user_prompt", "userPrompt", "prompt_user", "user"],
      userPromptRendered,
    );
    const userPromptTemplate = readField(entry, [
      "user_prompt_template",
      "userPromptTemplate",
      "template",
    ]);
    markUsed(["user_prompt_template", "userPromptTemplate", "template"], userPromptTemplate);
    const outputFormat = readField(entry, [
      "output_response_format",
      "outputResponseFormat",
      "response_format",
      "responseFormat",
      "output_format",
    ]);
    markUsed(
      ["output_response_format", "outputResponseFormat", "response_format", "responseFormat", "output_format"],
      outputFormat,
    );
    const aiResponse = readField(entry, [
      "ai_response_json",
      "aiResponseJson",
      "ai_response",
      "aiResponse",
      "response",
      "result",
      "output",
    ]);
    markUsed(
      ["ai_response_json", "aiResponseJson", "ai_response", "aiResponse", "response", "result", "output"],
      aiResponse,
    );
    const errorText = readField(entry, ["error", "error_message", "message"]);
    markUsed(["error", "error_message", "message"], errorText);

    const promptSections = [];
    const generalItems = [];
    const pushGeneral = (id, label, value, tone = "default") => {
      if (value === null || value === undefined || value === "") return;
      generalItems.push({ id, label, value, tone });
    };

    const statusLabel = statusRaw ? toLabel(statusRaw) : "Unknown";
    pushGeneral("status", "Status", statusLabel, "status");
    pushGeneral("model", "Model", model);
    pushGeneral("temperature", "Temperature", temperature);
    pushGeneral("order", "Order", order);
    pushGeneral("schema_type", "Schema Type", schemaType);
    if (errorText !== null && errorText !== undefined && errorText !== "") {
      pushGeneral("error", "Error", errorText, "error");
    }

    if (systemPrompt !== null && systemPrompt !== undefined && systemPrompt !== "") {
      promptSections.push({
        id: "system_prompt",
        label: "System Prompt",
        type: "text",
        tone: "default",
        value: systemPrompt,
      });
    }
    if (userPromptRendered !== null && userPromptRendered !== undefined && userPromptRendered !== "") {
      promptSections.push({
        id: "user_prompt_rendered",
        label: "User Prompt",
        type: "text",
        tone: "default",
        value: userPromptRendered,
      });
    }
    if (
      userPromptTemplate !== null &&
      userPromptTemplate !== undefined &&
      userPromptTemplate !== "" &&
      userPromptTemplate !== userPromptRendered
    ) {
      promptSections.push({
        id: "user_prompt_template",
        label: "User Template",
        type: "text",
        tone: "default",
        value: userPromptTemplate,
      });
    }
    if (outputFormat !== null && outputFormat !== undefined && outputFormat !== "") {
      promptSections.push({
        id: "output_response_format",
        label: "Output Response",
        type: "json",
        value: outputFormat,
      });
    }
    if (aiResponse !== null && aiResponse !== undefined && aiResponse !== "") {
      promptSections.push({
        id: "ai_response_json",
        label: "AI Response",
        type: "json",
        value: aiResponse,
      });
    }

    const additionalEntries = Object.entries(entry || {}).filter(
      ([key]) => !used.has(key) && !hiddenFieldKeys.has(String(key || "").trim()),
    );
    additionalEntries.forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      const isObjectValue = typeof value === "object" || isJsonValue(value);
      const isLongScalar = !isObjectValue && String(value).length > 180;
      if (isObjectValue || isLongScalar) {
        promptSections.push({
          id: `field_${key}`,
          label: toLabel(key),
          type: isObjectValue ? "json" : "text",
          tone: "default",
          value,
        });
      } else {
        pushGeneral(`meta_${key}`, toLabel(key), value);
      }
    });

    const sections = [
      {
        id: "general",
        label: "General",
        type: "general",
        value: generalItems,
      },
      ...promptSections,
    ];

    const displayName = schemaType ? String(schemaType) : `Derived Prompt ${index + 1}`;
    const titleLabel =
      order !== null && order !== undefined && order !== "" ? `${order}. ${displayName}` : displayName;

    return {
      titleLabel,
      statusRaw,
      statusLabel,
      model,
      temperature,
      sections,
    };
  };

  const selectedDerivedPromptDetail = useMemo(() => {
    if (!derivedPromptEntries.length) return null;
    const safeIndex = Math.min(
      Math.max(activeDerivedPromptIndex, 0),
      Math.max(derivedPromptEntries.length - 1, 0),
    );
    return buildDerivedPromptDetail(derivedPromptEntries[safeIndex], safeIndex);
  }, [derivedPromptEntries, activeDerivedPromptIndex]);

  const selectedOutputPromptDetail = useMemo(() => {
    if (!outputPromptEntries.length) return null;
    const safeIndex = Math.min(
      Math.max(activeOutputPromptIndex, 0),
      Math.max(outputPromptEntries.length - 1, 0),
    );
    return buildDerivedPromptDetail(outputPromptEntries[safeIndex], safeIndex);
  }, [outputPromptEntries, activeOutputPromptIndex]);

  useEffect(() => {
    const sections = selectedDerivedPromptDetail?.sections || [];
    if (!sections.length) {
      setActiveDerivedPromptSection("");
      return;
    }
    if (!sections.some((section) => section.id === activeDerivedPromptSection)) {
      setActiveDerivedPromptSection(sections[0].id);
    }
  }, [activeDerivedPromptSection, selectedDerivedPromptDetail]);

  useEffect(() => {
    const sections = selectedOutputPromptDetail?.sections || [];
    if (!sections.length) {
      setActiveOutputPromptSection("");
      return;
    }
    if (!sections.some((section) => section.id === activeOutputPromptSection)) {
      setActiveOutputPromptSection(sections[0].id);
    }
  }, [activeOutputPromptSection, selectedOutputPromptDetail]);

  const renderPromptExplorer = ({
    entries,
    tabItems,
    activeIndex,
    setActiveIndex,
    activeSection,
    setActiveSection,
    selectedDetail,
    emptyMessage,
    jsonViewKeyPrefix,
  }) => {
    if (!entries.length) {
      return <Typography sx={{ color: perlColors.textSecondary }}>{emptyMessage}</Typography>;
    }

    const selectedIndex = Math.min(Math.max(activeIndex, 0), Math.max(entries.length - 1, 0));
    const selectedSection = selectedDetail?.sections?.find((section) => section.id === activeSection);
    const selectedEntryKey = tabItems?.[selectedIndex]?.value ?? selectedIndex;

    return (
      <Box
        sx={{
          display: "flex",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            border: `1px solid ${perlColors.border}`,
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.2)",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "flex",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 210,
              minWidth: 210,
              maxWidth: 210,
              borderRight: `1px solid ${perlColors.border}`,
              bgcolor: "rgba(255,255,255,0.02)",
              overflowY: "auto",
              height: "100%",
            }}
          >
            <List disablePadding>
              {tabItems.map((tab) => (
                <ListItemButton
                  key={`prompt-left-${tab.value}`}
                  selected={selectedIndex === tab.value}
                  onClick={() => setActiveIndex(tab.value)}
                  sx={{
                    borderBottom: `1px solid ${perlColors.border}`,
                    py: 0.2,
                    "&.Mui-selected": {
                      bgcolor: "rgba(255,96,52,0.15)",
                    },
                  }}
                >
                  <ListItemText
                    primary={tab.label}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      color:
                        selectedIndex === tab.value ? perlColors.textPrimary : perlColors.textSecondary,
                      fontWeight: selectedIndex === tab.value ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 0.75,
              overflow: "hidden",
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.6,
            }}
          >
            {selectedDetail ? (
              <>
                <Box
                  sx={{
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: 1.25,
                    bgcolor: "rgba(255,255,255,0.02)",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <Tabs
                    value={activeSection}
                    onChange={(_event, value) => setActiveSection(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 34,
                      px: 0.2,
                      "& .MuiTab-root": {
                        minHeight: 34,
                        textTransform: "none",
                        color: perlColors.textSecondary,
                        fontSize: "0.74rem",
                        fontWeight: 500,
                        px: 0.85,
                        py: 0.3,
                      },
                      "& .Mui-selected": {
                        color: perlColors.textPrimary,
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#ff6034",
                        height: 2,
                      },
                    }}
                  >
                    {(selectedDetail.sections || []).map((section) => (
                      <Tab key={`prompt-section-tab-${section.id}`} value={section.id} label={section.label} />
                    ))}
                  </Tabs>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "stretch",
                  }}
                >
                  {selectedSection ? (
                    selectedSection.type === "json" ? (
                      renderPromptJsonPanel(
                        selectedSection.label,
                        selectedSection.value,
                        `${jsonViewKeyPrefix || "prompt_json"}_${selectedEntryKey}_${selectedSection.id}`,
                      )
                    ) : selectedSection.type === "general" ? (
                      renderPromptGeneralPanel(selectedSection.value)
                    ) : (
                      renderPromptTextPanel(
                        selectedSection.label,
                        selectedSection.value,
                        selectedSection.tone || "default",
                      )
                    )
                  ) : (
                    <Typography sx={{ color: perlColors.textSecondary }}>
                      Select a section to view details.
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Typography sx={{ color: perlColors.textSecondary, p: 1 }}>
                Select a prompt to view details.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderCostModelExplorer = () => {
    if (!costModelResultEntries.length) {
      return (
        <Typography sx={{ color: perlColors.textSecondary }}>
          No data found in aipc_run_log.CostModelResults for the selected run.
        </Typography>
      );
    }

    const selectedEntry =
      costModelResultEntries.find((entry) => entry.id === activeCostModelResultKey) ||
      costModelResultEntries[0];

    return (
      <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
        <Box
          sx={{
            border: `1px solid ${perlColors.border}`,
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.2)",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "220px minmax(0, 1fr)",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              borderRight: `1px solid ${perlColors.border}`,
              bgcolor: "rgba(255,255,255,0.02)",
              overflowY: "auto",
              height: "100%",
              minWidth: 0,
            }}
          >
            <List disablePadding>
              {costModelResultEntries.map((entry, index) => (
                <ListItemButton
                  key={`cost-model-entry-${entry.id}`}
                  selected={activeCostModelResultKey === entry.id}
                  onClick={() => setActiveCostModelResultKey(entry.id)}
                  sx={{
                    borderBottom: `1px solid ${perlColors.border}`,
                    py: 0.2,
                    "&.Mui-selected": {
                      bgcolor: "rgba(255,96,52,0.15)",
                    },
                  }}
                >
                  <ListItemText
                    primary={`${index + 1}. ${toLabel(entry.label)}`}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      color:
                        activeCostModelResultKey === entry.id
                          ? perlColors.textPrimary
                          : perlColors.textSecondary,
                      fontWeight: activeCostModelResultKey === entry.id ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              p: 0.75,
              overflow: "hidden",
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              justifyContent: "stretch",
            }}
          >
            {isJsonValue(selectedEntry?.value) || typeof selectedEntry?.value === "object"
              ? renderPromptJsonPanel(
                  toLabel(selectedEntry?.label),
                  selectedEntry?.value,
                  `cost_model_results_${selectedEntry?.id || "value"}`,
                )
              : renderPromptTextPanel(
                  toLabel(selectedEntry?.label),
                  selectedEntry?.value,
                  "default",
                )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderComparisonTable = () => {
    const comparisonRows = Array.isArray(comparisonData?.rows) ? comparisonData.rows : [];
    if (!comparisonRows.length) {
      return (
        <Typography sx={{ color: perlColors.textSecondary }}>
          No comparison data is available for the selected run.
        </Typography>
      );
    }

    const currentRunId = comparisonData?.current_run_id || "Current Run";
    const totalRow = comparisonRows.find((row) => String(row?.label || "").toLowerCase() === "total");
    const baselineTotal = Number(totalRow?.baseline);
    const currentTotal = Number(totalRow?.current);

    const renderComparisonCostCell = (value, isTotal) => {
      return (
        <Typography
          component="span"
          sx={{
            color: perlColors.textPrimary,
            fontWeight: isTotal ? 700 : 500,
            fontSize: "0.82rem",
            lineHeight: 1.1,
          }}
        >
          {formatComparisonCost(value)}
        </Typography>
      );
    };

    const renderComparisonPercentCell = (value, total, isTotal, tone = "#ff6034") => {
      const shareText = isTotal ? "100.0%" : formatComparisonShare(value, total);
      const shareValue = isTotal
        ? 100
        : Number.isFinite(Number(value)) && Number.isFinite(Number(total)) && Number(total) > 0
          ? Math.max(0, Math.min(100, (Number(value) / Number(total)) * 100))
          : 0;

      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.35 }}>
          {shareText ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, width: 145, justifyContent: "flex-end" }}>
              <Typography
                component="span"
                sx={{
                  color: perlColors.textSecondary,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  minWidth: 38,
                  textAlign: "right",
                  lineHeight: 1,
                }}
              >
                {shareText}
              </Typography>
              <Box
                sx={{
                  width: 90,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.08)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: `${shareValue}%`,
                    height: "100%",
                    borderRadius: 999,
                    bgcolor: tone,
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography
              component="span"
              sx={{
                color: perlColors.textSecondary,
                fontWeight: 600,
                fontSize: "0.72rem",
                lineHeight: 1.1,
              }}
            >
              {""}
            </Typography>
          )}
        </Box>
      );
    };

    return (
      <Box sx={{ width: "100%", maxWidth: 920, display: "flex", flexDirection: "column", gap: 1.2 }}>
        <Box
          sx={{
            border: `1px solid ${perlColors.border}`,
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.2)",
            px: 1.5,
            py: 1.2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 0.8 }}>
            <Typography sx={{ color: perlColors.textPrimary, fontWeight: 600, fontSize: "0.82rem" }}>
              Variance Threshold
            </Typography>
            <Typography sx={{ color: perlColors.textSecondary, fontSize: "0.8rem" }}>
              +/- {varianceThreshold}%
            </Typography>
          </Box>
          <Slider
            value={varianceThreshold}
            min={0}
            max={100}
            step={1}
            onChange={(_event, value) => setVarianceThreshold(Array.isArray(value) ? value[0] : value)}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
            sx={{
              color: "#ff6034",
              py: 0.5,
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
              },
              "& .MuiSlider-valueLabel": {
                bgcolor: "#ff6034",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            border: `1px solid ${perlColors.border}`,
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.2)",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <TableContainer sx={{ maxHeight: "100%" }}>
            <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Cost Breakdown
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Baseline ($)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Baseline (%)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Run {currentRunId} ($)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Run {currentRunId} (%)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Delta ($)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: "rgba(183, 158, 255, 0.18)",
                    color: perlColors.textPrimary,
                    fontWeight: 700,
                    py: 0.9,
                    px: 1.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Delta (%)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonRows.map((row) => {
                const isTotal = String(row?.label || "").toLowerCase() === "total";
                const hasDelta = Number.isFinite(Number(row?.delta_pct));
                const withinThreshold = hasDelta && Math.abs(Number(row?.delta_pct)) <= varianceThreshold;
                const deltaColor = !hasDelta
                  ? perlColors.textPrimary
                  : withinThreshold
                    ? "#62d394"
                    : "#ff6b6b";
                return (
                  <TableRow
                    key={row?.key || row?.label}
                    sx={{
                      bgcolor: isTotal ? "rgba(255,255,255,0.08)" : "transparent",
                      "& td": {
                        borderBottom: `1px solid ${perlColors.border}`,
                        py: 0.65,
                        px: 1.25,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: perlColors.textPrimary,
                        fontWeight: isTotal ? 700 : 500,
                        fontSize: "0.82rem",
                      }}
                    >
                      {row?.label || "-"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ minWidth: 88 }}
                    >
                      {renderComparisonCostCell(row?.baseline, isTotal)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ minWidth: 128 }}
                    >
                      {renderComparisonPercentCell(row?.baseline, baselineTotal, isTotal, "#8ab4ff")}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ minWidth: 88 }}
                    >
                      {renderComparisonCostCell(row?.current, isTotal)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ minWidth: 128 }}
                    >
                      {renderComparisonPercentCell(row?.current, currentTotal, isTotal, "#ff9d6c")}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: deltaColor,
                        fontWeight: isTotal ? 700 : 600,
                        fontSize: "0.82rem",
                        minWidth: 84,
                      }}
                    >
                      {formatComparisonDeltaValue(row?.current, row?.baseline)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: deltaColor,
                        fontWeight: isTotal ? 700 : 600,
                        fontSize: "0.82rem",
                      }}
                    >
                      {formatComparisonDelta(row?.delta_pct)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (activeTab === "schema") {
      return (
        <>
          {schemaTypes.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {schemaTypes.map((schemaType) => (
                <Typography
                  key={schemaType}
                  component="li"
                  sx={{ color: perlColors.textSecondary, mb: 0.5 }}
                >
                  {schemaType}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: perlColors.textSecondary }}>
              No schema types returned yet.
            </Typography>
          )}
          {!!statusMessage && (
            <Typography sx={{ mt: 2, color: perlColors.textSecondary }}>{statusMessage}</Typography>
          )}
        </>
      );
    }

    if (activeTab === "payload") {
      return requestPayload ? (
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "rgba(0,0,0,0.2)",
          }}
        >
          {renderJsonPanelWithToggle(requestPayload, "payload_json")}
        </Box>
      ) : (
        <Typography sx={{ color: perlColors.textSecondary }}>No payload captured yet.</Typography>
      );
    }

    if (activeTab === "schema_definitions") {
      if (!schemaDefinitions.length || !resolvedSchemaDefinitionColumns.length) {
        return (
          <Typography sx={{ color: perlColors.textSecondary }}>
            No schema definitions returned yet.
          </Typography>
        );
      }
      return renderDefinitionTable(
        schemaDefinitions,
        resolvedSchemaDefinitionColumns,
        schemaDefinitionColumnWidths,
      );
    }

    if (activeTab === "derived_schema_definitions") {
      if (!derivedSchemaDefinitions.length || !resolvedDerivedSchemaDefinitionColumns.length) {
        return (
          <Typography sx={{ color: perlColors.textSecondary }}>
            No derived schema definitions returned yet.
          </Typography>
        );
      }
      return renderDefinitionTable(
        derivedSchemaDefinitions,
        resolvedDerivedSchemaDefinitionColumns,
        derivedSchemaDefinitionColumnWidths,
      );
    }

    if (activeTab === "frontend_returns") {
      if (!Array.isArray(frontendReturnItems) || frontendReturnItems.length === 0) {
        return (
          <Typography sx={{ color: perlColors.textSecondary }}>
            No schema outputs were marked as return_to_frontend.
          </Typography>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {frontendReturnItems.map((item, index) => {
            const schemaType = item?.schema_type || `Schema_${index + 1}`;
            return (
              <Box
                key={`${schemaType}-${index}`}
                sx={{
                  border: `1px solid ${perlColors.border}`,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.2)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.7,
                    borderBottom: `1px solid ${perlColors.border}`,
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", color: perlColors.textPrimary }}>
                    {index + 1}. {schemaType}
                  </Typography>
                </Box>
                <Box sx={{ border: "none", borderRadius: 0, p: 1 }}>
                  <Box sx={{ height: 260, minHeight: 180 }}>
                    {renderJsonPanelWithToggle(item?.result ?? {}, `frontend_returns_${index}`, false)}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    }

    if (activeTab === "derived_prompts") {
      return renderPromptExplorer({
        entries: derivedPromptEntries,
        tabItems: derivedPromptTabItems,
        activeIndex: activeDerivedPromptIndex,
        setActiveIndex: setActiveDerivedPromptIndex,
        activeSection: activeDerivedPromptSection,
        setActiveSection: setActiveDerivedPromptSection,
        selectedDetail: selectedDerivedPromptDetail,
        emptyMessage: "No data found in aipc_run_log.DerivedPrompts for the selected run.",
        jsonViewKeyPrefix: "derived_prompts",
      });
    }

    if (activeTab === "output_schema_prompts") {
      return renderPromptExplorer({
        entries: outputPromptEntries,
        tabItems: outputPromptTabItems,
        activeIndex: activeOutputPromptIndex,
        setActiveIndex: setActiveOutputPromptIndex,
        activeSection: activeOutputPromptSection,
        setActiveSection: setActiveOutputPromptSection,
        selectedDetail: selectedOutputPromptDetail,
        emptyMessage: "No data found in aipc_run_log.OutputSchemaPrompts for the selected run.",
        jsonViewKeyPrefix: "output_schema_prompts",
      });
    }

    if (activeTab === "cost_model_results") {
      return renderCostModelExplorer();
    }

    if (activeTab === "comparison") {
      return renderComparisonTable();
    }

    return derivedCategorizationData ? (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "rgba(0,0,0,0.2)",
        }}
      >
        {renderJsonPanelWithToggle(derivedCategorizationData, "derived_categorization_json")}
      </Box>
    ) : (
      <Typography sx={{ color: perlColors.textSecondary }}>
        No data found in aipc_run_log.DerivedConstraints for the selected run.
      </Typography>
    );
  };

  const layoutNode = (
    <Box
      sx={{
        display: "flex",
        height: inline ? "100%" : "68vh",
        minHeight: inline ? 540 : 440,
      }}
    >
      <Box
        sx={{
          width: 240,
          minWidth: 240,
          maxWidth: 240,
          flex: "0 0 240px",
          borderRight: `1px solid ${perlColors.border}`,
          bgcolor: "rgba(255,255,255,0.02)",
          height: "100%",
          overflowY: "auto",
        }}
      >
        <List disablePadding>
          {TAB_ITEMS.map((tab) => (
            <ListItemButton
              key={tab.id}
              selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                borderBottom: `1px solid ${perlColors.border}`,
                "&.Mui-selected": {
                  bgcolor: "rgba(255,96,52,0.15)",
                },
              }}
            >
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  color: activeTab === tab.id ? perlColors.textPrimary : perlColors.textSecondary,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: 2.5,
          minWidth: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {activeTab !== "derived_prompts" &&
          activeTab !== "output_schema_prompts" &&
          activeTab !== "cost_model_results" &&
          activeTab !== "comparison" && (
          <Typography sx={{ mb: 1.5, fontWeight: 600 }}>{panelTitle}</Typography>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow:
              activeTab === "schema_definitions" ||
              activeTab === "derived_schema_definitions" ||
              activeTab === "derived_prompts" ||
              activeTab === "output_schema_prompts" ||
              activeTab === "cost_model_results" ||
              activeTab === "comparison"
                ? "hidden"
                : "auto",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );

  const cellDialogNode = (
    <Dialog
      open={cellDialogOpen}
      onClose={handleCloseCellDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: perlColors.surface,
          color: perlColors.textPrimary,
          border: `1px solid ${perlColors.border}`,
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${perlColors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>
          {selectedCell.column || "Cell Value"}
        </Typography>
        <IconButton onClick={handleCloseCellDialog} size="small" sx={actionIconSx}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ borderColor: perlColors.border }}>
        {selectedCell.isJson ? (
          <Box
            sx={{
              height: "55vh",
              minHeight: 320,
              border: `1px solid ${perlColors.border}`,
              borderRadius: 1.25,
              overflow: "hidden",
              bgcolor: "rgba(0,0,0,0.2)",
            }}
          >
            {renderJsonPanelWithToggle(selectedCell.value, "cell_dialog_json")}
          </Box>
        ) : (
          <Box component="pre" sx={preStyles}>
            {selectedCell.value}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  if (inline) {
    return (
      <>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: perlColors.surface,
            color: perlColors.textPrimary,
            border: "none",
            borderRadius: 0,
            overflow: "hidden",
          }}
        >
          {renderHeaderBar(false)}
          <Box sx={{ flex: 1, minHeight: 0 }}>{layoutNode}</Box>
        </Box>
        {cellDialogNode}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: perlColors.surface,
            color: perlColors.textPrimary,
            border: `1px solid ${perlColors.border}`,
            minHeight: 500,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ borderColor: perlColors.border, p: 0, overflow: "hidden" }}>
          {renderHeaderBar(true)}
          {layoutNode}
        </DialogContent>
      </Dialog>
      {cellDialogNode}
    </>
  );
}
